import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Global 401 interceptor: silently handle expired/invalid tokens
axios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('ad_master_token');
            delete axios.defaults.headers.common['Authorization'];
            if (window.location.pathname !== '/login' && window.location.pathname !== '/register') {
                window.location.href = '/login';
            }
            // Mark as auth error so pages can skip toast
            error._isAuthError = true;
        }
        return Promise.reject(error);
    }
);

// Readable French message for a failed request: network outage, permission,
// rate limit, then the server's own detail, then the caller's fallback.
export const errorMessage = (error, fallback) => {
    if (!error?.response) return 'Serveur injoignable. Vérifiez votre connexion puis réessayez.';
    const { status, data } = error.response;
    if (typeof data?.detail === 'string') return data.detail;
    if (status === 403) return "Vous n'avez pas les droits pour cette action.";
    if (status === 404) return "Élément introuvable : il a peut-être été supprimé.";
    if (status === 429) return 'Trop de requêtes. Patientez un instant puis réessayez.';
    if (status >= 500) return 'Erreur du serveur. Réessayez dans un instant.';
    return fallback;
};

// Projects
export const getProjects = (params = {}) => axios.get(`${API_URL}/projects`, { params });
export const getProject = (id) => axios.get(`${API_URL}/projects/${id}`);
export const createProject = (data) => axios.post(`${API_URL}/projects`, data);
export const updateProject = (id, data) => axios.put(`${API_URL}/projects/${id}`, data);
export const deleteProject = (id) => axios.delete(`${API_URL}/projects/${id}`);

// Masters
export const getMasters = (params = {}) => axios.get(`${API_URL}/masters`, { params });
export const getMaster = (id) => axios.get(`${API_URL}/masters/${id}`);
export const createMaster = (projectId, data) => axios.post(`${API_URL}/projects/${projectId}/masters`, data);
export const updateMaster = (id, data) => axios.put(`${API_URL}/masters/${id}`, data);
export const deleteMaster = (id) => axios.delete(`${API_URL}/masters/${id}`);
export const getLabelData = (id) => axios.get(`${API_URL}/masters/${id}/label`);

// Movements
export const getMovements = (params = {}) => axios.get(`${API_URL}/movements`, { params });
export const getMovement = (id) => axios.get(`${API_URL}/movements/${id}`);
export const createMovement = (data) => axios.post(`${API_URL}/movements`, data);
export const updateMovement = (id, data) => axios.put(`${API_URL}/movements/${id}`, data);
export const deleteMovement = (id) => axios.delete(`${API_URL}/movements/${id}`);

// Users
export const getUsers = () => axios.get(`${API_URL}/users`);
export const createUser = (data) => axios.post(`${API_URL}/users`, data);
export const updateUser = (id, data) => axios.put(`${API_URL}/users/${id}`, data);
export const deleteUser = (id) => axios.delete(`${API_URL}/users/${id}`);
export const resetUserPassword = (id, newPassword) => axios.put(`${API_URL}/users/${id}/reset-password`, { new_password: newPassword });

// Stats
export const getStats = () => axios.get(`${API_URL}/stats`);
export const getStockTable = () => axios.get(`${API_URL}/stats/stock-table`);

// SuperAdmin
export const getAllCompanies = () => axios.get(`${API_URL}/superadmin/companies`);
export const switchCompany = (companyId) => axios.post(`${API_URL}/superadmin/switch-company`, { company_id: companyId });
export const createCompany = (data) => axios.post(`${API_URL}/superadmin/companies`, data);
export const deleteCompany = (companyId) => axios.delete(`${API_URL}/superadmin/companies/${companyId}`);
export const setCompanyPaused = (companyId, paused) => axios.put(`${API_URL}/superadmin/companies/${companyId}/pause`, { paused });

// Import
export const previewImport = (formData) => axios.post(`${API_URL}/superadmin/import/preview`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});
export const confirmImport = (formData) => axios.post(`${API_URL}/superadmin/import/confirm`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});

// Constants
export const MASTER_STATUSES = {
    en_stock: 'En stock',
    hors_stock: 'Hors stock',
    chez_client: 'Chez le client',
    perdu: 'Perdu',
    hors_service: 'Hors service',
    ecrase: 'Écrasé'
};

export const MASTER_FORMATS = [
    '2K flat',
    '2K scope',
    '4K flat',
    '4K scope'
];

export const FRAME_RATES = ['24p', '25p'];

export const MOVEMENT_TYPES = {
    reception: 'Réception',
    expedition: 'Livraison',
    transfert: 'Transfert'
};

export const DELIVERY_MODES = {
    coursier: 'Coursier',
    chronopost: 'Chronopost',
    colissimo: 'Colissimo',
    la_poste: 'La Poste',
    autre: 'Autre'
};

export const USER_ROLES = {
    superadmin: 'Super Admin',
    admin: 'Administrateur',
    manager: 'Manager',
    user: 'Utilisateur'
};

export const LANGUAGES = [
    'Français', 'Anglais', 'Allemand', 'Espagnol', 'Italien', 
    'Japonais', 'Coréen', 'Mandarin', 'Portugais', 'Russe',
    'Arabe', 'Hindi', 'Néerlandais', 'Polonais', 'Suédois'
];
